import {
  type ForkTemplateSchema,
  forkTemplateSchema,
} from "@gearbox-protocol/deploy-tools-shared";
import { ErrorBox } from "@gearbox-protocol/deploy-tools-ui";
import { api } from "api";
import ForkForm from "components/fork-form";
import { type FC, useMemo } from "react";
import type { FileWithPath } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { useAsync } from "react-use";
import { useForkStatus } from "routes/fork/context";
import { loadFiles } from "routes/fork/loadFiles";

const UpdateForkRoute: FC = () => {
  const status = useForkStatus();
  const { options, alias } = status;
  const navigate = useNavigate();

  const defaultOptions = useMemo((): ForkTemplateSchema => {
    return forkTemplateSchema.parse(options);
  }, [options]);

  const {
    value: defaultFiles,
    loading,
    error: loadError,
  } = useAsync(async () => {
    if (status.paused) {
      return [];
    }
    return loadFiles(status.executionId, status.files);
  }, [status]);

  const onSubmit = useMemo(
    () => async (data: ForkTemplateSchema, files: FileWithPath[]) => {
      if (!alias) {
        throw new Error("Fork alias is not set");
      }
      // call parse to cleanup
      const input = forkTemplateSchema.parse(data);
      const resp = await api.updateFork(alias, input, files);
      await navigate(`/forks/${resp.executionId}`);
    },
    [navigate, alias],
  );

  if (loadError) {
    return <ErrorBox error={loadError} />;
  }

  if (loading || defaultFiles === undefined) {
    return null;
  }

  return (
    <ForkForm
      title="Fork Configuration"
      submitButtonText="Update Fork"
      defaultValues={defaultOptions}
      defaultFiles={defaultFiles}
      hideAlias={true}
      onSubmit={onSubmit}
    />
  );
};

export default UpdateForkRoute;
